import React from 'react'
import { Card, Tag, Badge, Space, Button, Typography, Row, Col, Tooltip, Popconfirm } from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  InfoCircleOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { getStockStatus, getExpiryStatus, formatCurrency, formatDate } from '../../utils/helpers'
import '../../../../styles/InventoryComponents.css'

const { Text, Paragraph } = Typography

const InventoryItemCard = ({ item, onEdit, onDelete, onOrder, showActions = true }) => {
  // Calculate status for display
  const stockStatus = getStockStatus(item)
  const expiryStatus = item.expiryDate ? getExpiryStatus(item.expiryDate) : null

  // Generate status badge
  const getStatusBadge = () => {
    // Priority: Expired > Critical Expiry > Low Stock > Over Stock > Healthy
    if (expiryStatus && expiryStatus.status === 'Expired') {
      return <Badge count='Expired' style={{ backgroundColor: expiryStatus.color }} />
    }

    if (expiryStatus && expiryStatus.status === 'Critical Expiry') {
      return <Badge count='Expiring Soon' style={{ backgroundColor: expiryStatus.color }} />
    }

    if (stockStatus.status === 'Out of Stock') {
      return <Badge count='Out of Stock' style={{ backgroundColor: stockStatus.color }} />
    }

    if (stockStatus.status === 'Low Stock') {
      return <Badge count='Low Stock' style={{ backgroundColor: stockStatus.color }} />
    }

    if (stockStatus.status === 'Over Stock') {
      return <Badge count='Over Stock' style={{ backgroundColor: stockStatus.color }} />
    }

    return null
  }

  return (
    <Card
      className='inventory-item-card'
      actions={
        showActions
          ? [
              <Tooltip title='Edit'>
                <EditOutlined key='edit' onClick={() => onEdit(item)} />
              </Tooltip>,
              <Tooltip title='Order'>
                <ShoppingCartOutlined key='order' onClick={() => onOrder(item)} />
              </Tooltip>,
              <Popconfirm
                title='Are you sure you want to delete this item?'
                onConfirm={() => onDelete(item.id)}
                okText='Yes'
                cancelText='No'
              >
                <DeleteOutlined key='delete' />
              </Popconfirm>
            ]
          : []
      }
    >
      <div className='inventory-card-badge'>{getStatusBadge()}</div>

      <div className='inventory-card-content'>
        <Text strong className='inventory-card-title' title={item.name}>
          {item.name}
        </Text>

        <Paragraph type='secondary' ellipsis={{ rows: 2 }}>
          {item.description || 'No description'}
        </Paragraph>

        <div className='inventory-card-detail'>
          <Text>Category:</Text>
          <Text>{item.category}</Text>
        </div>

        <div className='inventory-card-detail'>
          <Text>Quantity:</Text>
          <Tag color={stockStatus.color}>{item.quantityOnHand}</Tag>
        </div>

        <div className='inventory-card-detail'>
          <Text>Min/Max:</Text>
          <Text>
            {item.minStockLevel} / {item.maxStockLevel}
          </Text>
        </div>

        <div className='inventory-card-detail'>
          <Text>Cost:</Text>
          <Text>{formatCurrency(item.unitCost)}</Text>
        </div>

        {item.expiryDate && (
          <div className='inventory-card-detail'>
            <Text>Expires:</Text>
            <Tag color={expiryStatus.color}>{formatDate(item.expiryDate)}</Tag>
          </div>
        )}

        <div className='inventory-card-detail'>
          <Text>Supplier:</Text>
          <Text ellipsis>{item.supplier.name}</Text>
        </div>

        <div className='inventory-card-detail'>
          <Text>Department:</Text>
          <Text>{item.department}</Text>
        </div>
      </div>
    </Card>
  )
}

export default InventoryItemCard
